import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import skandorLogo from "@/assets/skandor-logo.svg";
import { bookingHref, bookingTarget, handleBookingClick } from "@/lib/booking";

const capabilityLinks = [
  { href: "/capabilities", label: "Capabilities overview" },
  { href: "/approach", label: "Strategy & Transformation" },
  { href: "/services/product", label: "AI & Product" },
  { href: "/services/go-to-market", label: "Go-to-Market" },
  { href: "/services/operations", label: "Operations & Resilience" },
];

const navLinks = [
  { href: "/approach", label: "Approach", isSection: false },
  { href: "/capabilities", label: "Capabilities", isSection: false, hasMenu: true },
  { href: "/work", label: "Work", isSection: false },
  { href: "/about", label: "About", isSection: false },
  { href: "/organizations", label: "Organizations", isSection: false },
  { href: "/careers", label: "Careers", isSection: false },
];

const FALLBACK_HEADER_OFFSET = 80;

const getHeaderOffset = () => {
  const header = document.querySelector("header") as HTMLElement | null;
  const measured = header?.getBoundingClientRect().height ?? 0;
  // Add a small breathing gap so the section title isn't flush against the header
  const gap = 12;
  return (measured > 0 ? measured : FALLBACK_HEADER_OFFSET) + gap;
};

const scrollToHash = (hash: string) => {
  const el = document.querySelector(hash) as HTMLElement | null;
  if (!el) return;
  const doScroll = () => {
    const offset = getHeaderOffset();
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
  };
  // Run twice: once now, once after layout settles (fonts/images/orientation change)
  doScroll();
  window.setTimeout(doScroll, 250);
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [accentBooking, setAccentBooking] = useState(false);
  const accentedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 8);
      const hero = document.getElementById("hero");
      if (!accentedRef.current && hero && window.scrollY > hero.offsetHeight) {
        accentedRef.current = true; setAccentBooking(true); window.setTimeout(() => setAccentBooking(false), 600);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Body scroll lock + focus trap + Escape handling while mobile menu is open
  useEffect(() => {
    if (!isMobileMenuOpen) {
      document.body.classList.remove("no-scroll");
      // Restore focus to trigger
      if (previouslyFocusedRef.current) {
        previouslyFocusedRef.current.focus?.();
        previouslyFocusedRef.current = null;
      }
      return;
    }

    document.body.classList.add("no-scroll");
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    const getFocusable = (): HTMLElement[] => {
      const panel = menuPanelRef.current;
      if (!panel) return [];
      return Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute("aria-hidden"));
    };

    // Move focus into the panel
    requestAnimationFrame(() => {
      const focusables = getFocusable();
      focusables[0]?.focus();
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setIsMobileMenuOpen(false);
        menuToggleRef.current?.focus();
        return;
      }
      if (e.key !== "Tab") return;
      const focusables = getFocusable();
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !menuPanelRef.current?.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("no-scroll");
    };
  }, [isMobileMenuOpen]);

  const handleNavClick = (href: string, isSection: boolean) => {
    // Close menu first so overlay unmounts and scroll lock releases
    setIsMobileMenuOpen(false);

    if (isSection) {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => scrollToHash(href), 120);
      } else {
        // Delay a frame to allow scroll-lock release before scrolling
        requestAnimationFrame(() => scrollToHash(href));
      }
    } else {
      window.scrollTo(0, 0);
      navigate(href);
    }
  };

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-ink"
      >
        Skip to main content
      </a>
      <header
        className={`fixed top-0 left-0 right-0 z-50 h-[74px] transition-all duration-250 ${
          isScrolled
            ? "border-b border-line bg-background/[.88] backdrop-blur-[10px]"
            : "bg-background/75 backdrop-blur-[10px]"
        }`}
      >
        <div className="container mx-auto flex h-full items-center justify-between relative z-10">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("#hero", true);
            }}
            className="flex items-center"
            aria-label="Skandor Technologies — home"
          >
            <img
              src={skandorLogo}
              alt="Skandor Technologies"
              width={210}
              height={56}
              decoding="async"
              className="h-11 w-auto"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => {
              const href = link.isSection ? `/${link.href}` : link.href;
              const isActive =
                !link.isSection &&
                (location.pathname === link.href ||
                  (link.href === "/capabilities" &&
                    (location.pathname === "/capabilities" ||
                      location.pathname.startsWith("/services"))));
              const anchor = (
                <a
                  href={href}
                  aria-current={isActive ? "page" : undefined}
                  aria-haspopup={link.hasMenu ? "true" : undefined}
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                    e.preventDefault();
                    handleNavClick(link.href, link.isSection);
                  }}
                   className={`relative text-sm font-medium transition-colors duration-250 nav-link-underline min-h-11 inline-flex items-center px-1 ${
                     isActive ? "text-steel-2 active" : "text-ink hover:text-steel-2"
                  }`}
                >
                  {link.label}
                </a>
              );

              if (!link.hasMenu) return <div key={link.href}>{anchor}</div>;

              return (
                <div key={link.href} className="relative group">
                  {anchor}
                  <div
                    className="absolute left-0 top-full pt-2 w-64 opacity-0 invisible translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0"
                  >
                    <ul className="bg-card border border-border rounded-xl shadow-[var(--shadow-card)] py-2">
                      {capabilityLinks.map((sub) => (
                        <li key={sub.href}>
                          <Link
                            to={sub.href}
                            onClick={() => window.scrollTo(0, 0)}
                            aria-current={location.pathname === sub.href ? "page" : undefined}
                            className={`flex items-center min-h-11 px-4 text-sm transition-colors ${
                              location.pathname === sub.href
                                ? "text-steel-2 font-semibold"
                                : "text-ink hover:text-steel-2 hover:bg-muted"
                            }`}
                          >
                            {sub.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </nav>

          <Button
            asChild
            variant="primary"
             className={`hidden lg:flex ${accentBooking ? "shadow-[inset_0_-2px_0_#E0B63C,var(--shadow-button)]" : ""}`}
          >
             <a href={bookingHref} target={bookingTarget} rel={bookingTarget ? "noopener noreferrer" : undefined} onClick={(event) => handleBookingClick(event, "navbar")}>Book a diagnostic call →</a>
          </Button>


          <button
            ref={menuToggleRef}
            onClick={() => setIsMobileMenuOpen((s) => !s)}
            className="lg:hidden p-2.5 transition-colors text-ink relative z-[70]"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav"
          >
            {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      {/* Full-height mobile overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
               className="lg:hidden fixed inset-0 z-40 bg-ink/20 backdrop-blur-[2px]"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-hidden="true"
            />
            <motion.div
              key="panel"
              ref={menuPanelRef}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              id="mobile-nav"
              role="dialog"
              aria-modal="true"
              aria-label="Main navigation"
              onClick={(e) => {
                // Close when clicking the panel's empty area (outside the nav content)
                if (e.target === e.currentTarget) setIsMobileMenuOpen(false);
              }}
               className="lg:hidden fixed inset-0 top-0 z-[45] bg-background pt-24 pb-10 px-6 overflow-y-auto"
            >
              <nav className="flex flex-col gap-1">
                {navLinks.map((link) => {
                  const href = link.isSection ? `/${link.href}` : link.href;
                  return (
                    <div key={link.href}>
                      <a
                        href={href}
                        onClick={(e) => {
                          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                          e.preventDefault();
                          handleNavClick(link.href, link.isSection);
                        }}
                         className="block text-base font-semibold font-heading font-semibold text-ink hover:text-steel-2 transition-colors py-4 min-h-[48px] text-left border-b border-line"
                      >
                        {link.label}
                      </a>
                      {link.hasMenu && (
                        <ul className="pl-4 border-l border-border/60 ml-1">
                          {capabilityLinks.slice(1).map((sub) => (
                            <li key={sub.href}>
                              <a
                                href={sub.href}
                                onClick={(e) => {
                                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                                  e.preventDefault();
                                  handleNavClick(sub.href, false);
                                }}
                                className="flex items-center min-h-[48px] text-base text-ink-2 hover:text-steel-2 transition-colors border-b border-border/40"
                              >
                                {sub.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
                <Button
                  asChild
                  variant="primary"
                  className="mt-8 w-full min-h-[48px] py-3"
                >
                   <a href={bookingHref} target={bookingTarget} rel={bookingTarget ? "noopener noreferrer" : undefined} onClick={(event) => { setIsMobileMenuOpen(false); handleBookingClick(event, "navbar_mobile"); }}>Book a diagnostic call →</a>
                </Button>
                <a
                  href="/#contact"
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                    e.preventDefault();
                    handleNavClick("#contact", true);
                  }}
                  className="mt-4 text-base text-ink-2 underline underline-offset-4 decoration-border hover:text-steel-2"
                >
                  Talk to a senior partner
                </a>
              </nav>

            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
